import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { Task } from '@/src/renderer/lib/db/models'
import type { NestPickerGroup } from '../components/NestPicker'

/**
 * **Wohin eine Unteraufgabe umgehängt werden kann** (#204, #205): die offenen Aufgaben der
 * Liste, in der ihre Aufgabe steht, nach Abschnitt. Die eigene Aufgabe und erledigte sind grau —
 * und wiederkehrende, wenn sich eine der Umzuhängenden wiederholt (`movingRecurring`).
 *
 * Aus der Datenquelle und nicht von der Liste: Die Aufgaben-Details stehen neben der Liste,
 * und das Menü an der Checkliste gibt es auch in Demnächst.
 */
export function useMoveTargets(parent: Task | null | undefined, movingRecurring = false): NestPickerGroup[] {
  const { t } = useTranslation()
  const source = useDataSource()
  const projectTasks = source.useTasksByProject(parent?.projectId ?? undefined)
  const quickNotes = source.useQuickNotes(parent?.lifeSpace ?? 'personal')
  const sections = source.useSectionsByProject(parent?.projectId ?? undefined)
  const listed = parent?.projectId ? projectTasks : quickNotes

  return useMemo<NestPickerGroup[]>(() => {
    if (!parent) return []
    const tasks = (listed ?? []).filter(task => task.parentTaskId == null).sort((a, b) => a.order - b.order)
    const option = (task: Task) => ({
      id: task.id,
      title: task.title || t('taskList.nest.untitled'),
      disabled: task.id === parent.id || task.status === 'completed' || (movingRecurring && task.recurrence != null),
    })
    const loose = { id: 'none', title: null, options: tasks.filter(task => !task.sectionId).map(option) }
    return [loose, ...[...sections].sort((a, b) => a.order - b.order).map(section => ({
      id: section.id,
      title: section.title,
      options: tasks.filter(task => task.sectionId === section.id).map(option),
    }))]
  }, [listed, sections, parent, t, movingRecurring])
}
